/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent } from 'react';
import PropTypes from 'prop-types';
import { ProgramType } from 'client-code/airing';
import { DESCRIPTION_MAX_LENGTH } from '../../../../config';
import { InfoBannerUtils } from './InfoBannerUtils';
import { EventDescriptionProps } from './EventDescriptionProps';
import { Locale } from '../../../../core/locale/Locale';
import { LocaleKeys } from '../../../../core/locale/LocaleKeys';
import { VideoPlayerModelImpl } from '../../../../core/videoplayer/impls';

import './EventDescription.scss';

const EventDescription: FunctionComponent<EventDescriptionProps> = (props): JSX.Element => {
    const { mAiringData, mPreviewData } = props;

    const EMPTY_SPACE = <span>&nbsp;</span>;
    const LEFT_QUOTE = <span>&#8216;</span>;
    const RIGHT_QUOTE = <span>&#8217;</span>;
    const COMMA = <span>,</span>;
    const DASH = <span>&#8211;</span>;

    const dataFormatter = new InfoBannerUtils(mAiringData, mPreviewData);

    const description = dataFormatter.getDescription();

    const cast = dataFormatter.getCast();

    const genre = dataFormatter.getGenre();

    const firstAiredDate = dataFormatter.getFirstAireddate();

    const episodeTitle = dataFormatter.getEpisodeTitle();

    const programType = dataFormatter.getProgramType();

    const isAdult = dataFormatter.hasAdult();

    const showAttributes = dataFormatter.getShowAttributes(
        VideoPlayerModelImpl.getInstance().getPlayerStats().height,
    );

    let hasTruncation = false;

    const episodeTitleLength = episodeTitle.length ? episodeTitle.length + 3 : 0;

    const descriptionLength = episodeTitleLength + description.length;

    let genreCastSeperator = false;
    if ((dataFormatter.hasGenre() || firstAiredDate) && cast && !isAdult) {
        genreCastSeperator = true;
    }

    const genreCastSeperatorDiv = (
        <div
            className="genre-cast-seperator"
            data-testid="event-description-genre-cast-separator"
        ></div>
    );

    const episodeTitleDiv = episodeTitle && (
        <span data-testid="event-description-episode-title">
            {LEFT_QUOTE}
            {episodeTitle}
            {RIGHT_QUOTE}
            {DASH}
            {EMPTY_SPACE}
        </span>
    );

    if (descriptionLength >= DESCRIPTION_MAX_LENGTH && !isAdult) {
        hasTruncation = true;
    }

    const metaDataDiv = (
        <div className="metadata-container" data-testid="event-description-metadata-container">
            <div className="genre" data-testid="event-description-genre">
                {genre ? <>{genre}</> : null}
                <span>
                    {genre && firstAiredDate && programType !== ProgramType.Movie ? (
                        <>
                            {COMMA} {EMPTY_SPACE}
                        </>
                    ) : null}
                    {programType !== ProgramType.Movie ? <>{firstAiredDate} </> : null}
                </span>
            </div>
            {genreCastSeperator ? genreCastSeperatorDiv : null}
            {cast && !isAdult ? (
                <div className="cast" data-testid="event-description-cast">
                    {cast}
                </div>
            ) : null}
        </div>
    );

    return (
        <div className="event-description-container">
            <div className="description" data-testid="event-description">
                {programType === ProgramType.Episode ? <>{episodeTitleDiv}</> : null}
                {isAdult ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN) : description}
                {EMPTY_SPACE}
                {showAttributes ? (
                    <div
                        className={`show-attributes-container ${hasTruncation ? 'truncate' : ''}`}
                        data-testid="event-description-show-attributes"
                    >
                        {hasTruncation ? <div className="transparent-effect"></div> : null}
                        <div className={hasTruncation ? 'show-attributes-truncate' : ''}>
                            <span data-testid="show-attributes">({showAttributes})</span>
                        </div>
                    </div>
                ) : null}
            </div>
            {metaDataDiv}
        </div>
    );
};
EventDescription.propTypes = {
    mAiringData: PropTypes.any.isRequired,
    mPreviewData: PropTypes.any.isRequired,
};

export default EventDescription;
